import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Chip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  People as PeopleIcon,
  Cancel as FailureIcon,
  Description as FormIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

// Segmented Control Component
const SegmentedControl = ({ value, onChange, options }) => {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        bgcolor: 'grey.100',
        borderRadius: 2,
        p: 0.5,
        gap: 0.5,
      }}
    >
      {options.map((option) => (
        <Box
          key={option.value}
          onClick={() => onChange(option.value)}
          sx={{
            px: 2,
            py: 1,
            borderRadius: 1.5,
            cursor: 'pointer',
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: value === option.value ? 'grey.900' : 'grey.500',
            bgcolor: value === option.value ? 'background.paper' : 'transparent',
            boxShadow: value === option.value ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.2s ease',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            '&:hover': {
              color: value === option.value ? 'grey.900' : 'grey.700',
            },
          }}
        >
          {option.label}
        </Box>
      ))}
    </Box>
  );
};

// Modern Metric Card Component
const MetricCard = ({ title, value, icon: Icon, colorScheme, subtitle, onClick, accentValue = false }) => {
  // Guard against a metric the backend hasn't started returning yet (e.g. a
  // rolling deploy where the frontend ships ahead of the API).
  const safeValue = value ?? 0;
  const isZero = safeValue === 0;

  // Color schemes for different card types
  const colorSchemes = {
    primary: {
      iconBg: '#EFF6FF',
      iconColor: '#2563EB',
      valueColor: '#111827',
    },
    success: {
      iconBg: '#ECFDF5',
      iconColor: '#10B981',
      valueColor: '#10B981',
    },
    warning: {
      iconBg: '#FEF3C7',
      iconColor: '#F59E0B',
      valueColor: '#D97706',
    },
    info: {
      iconBg: '#ECFEFF',
      iconColor: '#06B6D4',
      valueColor: '#111827',
    },
    secondary: {
      iconBg: '#F5F3FF',
      iconColor: '#7C3AED',
      valueColor: '#111827',
    },
  };

  const colors = colorSchemes[colorScheme] || colorSchemes.primary;

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        '&:hover': onClick ? {
          borderColor: 'grey.300',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          transform: 'translateY(-2px)',
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box
            sx={{
              backgroundColor: colors.iconBg,
              borderRadius: 2,
              p: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ color: colors.iconColor, fontSize: 22 }} />
          </Box>
          {onClick && (
            <Box
              sx={{
                fontSize: '0.75rem',
                color: 'grey.400',
                fontWeight: 500,
              }}
            >
              Click to view
            </Box>
          )}
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            fontSize: '1.75rem',
            lineHeight: 1.2,
            color: isZero ? 'grey.300' : (accentValue ? colors.valueColor : 'grey.900'),
            mb: 0.5,
          }}
        >
          {safeValue.toLocaleString()}
        </Typography>

        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'grey.600',
            mb: 0.25,
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: 'grey.400',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

function Analytics() {
  const [dateFilter, setDateFilter] = useState('7days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLogs, setDialogLogs] = useState([]);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);

  const dateOptions = [
    { value: 'today', label: 'Today' },
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: 'custom', label: 'Custom' },
  ];

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, startDate, endDate]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};

      if (dateFilter !== 'custom') {
        params.preset = dateFilter;
      } else if (startDate && endDate) {
        params.start_date = new Date(startDate).toISOString();
        params.end_date = new Date(endDate).toISOString();
      }

      const summaryResponse = await adminAPI.getAnalyticsSummary(params);
      setSummary(summaryResponse.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async (activityType, status, title) => {
    setDialogLoading(true);
    setDialogTitle(title);
    setDialogOpen(true);
    setDialogLogs([]);

    try {
      const params = {};

      if (dateFilter !== 'custom') {
        params.preset = dateFilter;
      } else if (startDate && endDate) {
        params.start_date = new Date(startDate).toISOString();
        params.end_date = new Date(endDate).toISOString();
      }

      if (activityType) params.activity_type = activityType;
      if (status) params.status = status;

      const response = await adminAPI.getActivityLogs(params);
      setDialogLogs(response.data);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setError(err.response?.data?.detail || 'Failed to load activity logs');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCardClick = (activityType, status, title) => {
    fetchActivityLogs(activityType, status, title);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogLogs([]);
  };

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
  };

  if (loading && !summary) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress size={32} sx={{ color: 'grey.400' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400 }}>
      {/* Date Filter Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <SegmentedControl
          value={dateFilter}
          onChange={handleDateFilterChange}
          options={dateOptions}
        />

        {dateFilter === 'custom' && (
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <TextField
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                  fontSize: '0.875rem',
                },
              }}
            />
            <Typography sx={{ color: 'grey.400', fontSize: '0.875rem' }}>to</Typography>
            <TextField
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'error.lighter',
          }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} sx={{ color: 'grey.400' }} />
        </Box>
      ) : summary && (
        <>
          {/* Primary Metrics Row */}
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} md={4}>
              <MetricCard
                title="Total Users"
                value={summary.total_unique_users}
                icon={PeopleIcon}
                colorScheme="primary"
                subtitle="Unique active users"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetricCard
                title="RAG Success"
                value={summary.rag_queries_success}
                icon={ChatIcon}
                colorScheme="success"
                subtitle="Successful queries"
                accentValue={true}
                onClick={() => handleCardClick('RAG_QUERY', 'SUCCESS', 'Successful RAG Queries')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetricCard
                title="RAG No Response"
                value={summary.rag_queries_no_response}
                icon={FailureIcon}
                colorScheme="warning"
                subtitle="Queries with no info"
                accentValue={true}
                onClick={() => handleCardClick('RAG_QUERY', 'FAILURE', 'Failed RAG Queries (No Response)')}
              />
            </Grid>
          </Grid>

          {/* Secondary Metrics Row */}
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="HBNC Created"
                value={summary.hbnc_forms_created}
                icon={FormIcon}
                colorScheme="info"
                subtitle="New forms"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="HBNC Updated"
                value={summary.hbnc_forms_updated}
                icon={EditIcon}
                colorScheme="secondary"
                subtitle="Form updates"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="HBYC Created"
                value={summary.hbyc_forms_created}
                icon={FormIcon}
                colorScheme="info"
                subtitle="New forms"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="HBYC Updated"
                value={summary.hbyc_forms_updated}
                icon={EditIcon}
                colorScheme="secondary"
                subtitle="Form updates"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="NRC Created"
                value={summary.nrc_forms_created}
                icon={FormIcon}
                colorScheme="info"
                subtitle="New forms"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="NRC Updated"
                value={summary.nrc_forms_updated}
                icon={EditIcon}
                colorScheme="secondary"
                subtitle="Form updates"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="SNCU Created"
                value={summary.sncu_forms_created}
                icon={FormIcon}
                colorScheme="info"
                subtitle="New forms"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="SNCU Updated"
                value={summary.sncu_forms_updated}
                icon={EditIcon}
                colorScheme="secondary"
                subtitle="Form updates"
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Activity Logs Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            py: 2,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
            {dialogTitle}
          </Typography>
          <IconButton
            onClick={handleCloseDialog}
            size="small"
            sx={{
              color: 'grey.400',
              '&:hover': { color: 'grey.600', bgcolor: 'grey.100' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {dialogLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={32} sx={{ color: 'grey.400' }} />
            </Box>
          ) : dialogLogs.length === 0 ? (
            <Box py={6} textAlign="center">
              <Typography sx={{ color: 'grey.400', fontSize: '0.875rem' }}>
                No logs found for selected criteria
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: 'grey.600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 1.5,
                      }}
                    >
                      Time
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: 'grey.600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 1.5,
                      }}
                    >
                      User
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: 'grey.600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 1.5,
                      }}
                    >
                      Query/Info
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: 'grey.600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 1.5,
                      }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialogLogs.map((log, index) => (
                    <TableRow
                      key={log.id || index}
                      sx={{
                        '&:hover': { bgcolor: 'grey.50' },
                        '&:last-child td': { borderBottom: 0 },
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'grey.500' }}>
                          {new Date(log.timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'grey.900' }}>
                          {log.username}
                        </Typography>
                        {log.phone_number && (
                          <Typography sx={{ fontSize: '0.75rem', color: 'grey.400' }}>
                            {log.phone_number}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2, maxWidth: 400 }}>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: 'grey.700',
                            wordBreak: 'break-word',
                          }}
                        >
                          {log.info || '-'}
                        </Typography>
                        {log.metadata?.language && (
                          <Chip
                            label={log.metadata.language.toUpperCase()}
                            size="small"
                            sx={{
                              mt: 0.5,
                              height: 20,
                              fontSize: '0.625rem',
                              fontWeight: 600,
                              bgcolor: 'grey.100',
                              color: 'grey.600',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={log.status}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: log.status === 'SUCCESS' ? 'success.lighter' : 'error.lighter',
                            color: log.status === 'SUCCESS' ? 'success.dark' : 'error.dark',
                            border: 'none',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid',
            borderColor: 'grey.200',
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: 'grey.600',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Analytics;
